import { useGetActiveLeadSource } from '../../hooks'
import CampaignService from '@forwardflow/services/CampaignService'
import { useCampaignActivationModalStore } from '@forwardflow/store/modal/campaignActivationModal'
import { usePhoneNumberUnavailableModalStore } from '@forwardflow/store/modal/phoneNumberUnavailableModal'
import { Button } from '@forwardflow/ui-v2'
import Icon from '@forwardflow/ui/Icons'
import { message } from 'antd'
import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

export const TopBar = () => {
   const { openCampaignActivationModal } = useCampaignActivationModalStore()
   const [searchParams] = useSearchParams()
   const campaignName = searchParams.get('campaignName')
   const [isStatusChanging, setIsStatusChanging] = useState(false)
   const [isLoading, setIsLoading] = useState(false)
   const [isCampaignActive, setIsCampaignActive] = useState(false)
   const { title } = useParams()
   const { setUnavailablePhoneNumbers, openPhoneNumberUnavailableModal } =
      usePhoneNumberUnavailableModalStore()
   const { data: activeLeadSourceDetails, mutate } = useGetActiveLeadSource(
      title || ''
   )

   const [showSaveButton, setShowSaveButton] = useState(false)

   useEffect(() => {
      if (activeLeadSourceDetails) {
         setIsCampaignActive(activeLeadSourceDetails.status === 'active')

         setShowSaveButton(
            activeLeadSourceDetails?.draft?.changedSections?.length > 0
         )
      }
   }, [activeLeadSourceDetails])

   const onCampaignStatusChange = async () => {
      setIsStatusChanging(true)
      try {
         await CampaignService.updateCampaignStatus(
            title as string,
            isCampaignActive ? 'paused' : 'active'
         )
         message.success('Campaign status updated successfully')
         mutate()
      } catch (error) {
         message.error('Failed to update campaign status')
      } finally {
         setIsStatusChanging(false)
      }
   }

   const ApplyDraftCampaign = async () => {
      try {
         setIsLoading(true)

         const response = await CampaignService.applyDraftCampaign(
            title as string
         )
         if (response?.hasFailedPurchase) {
            const phoneNumberArray: string[] = []
            response?.purchaseResult?.failedPurchases.map((purchase: any) => {
               phoneNumberArray.push(purchase.phoneNumber)
            })
            setUnavailablePhoneNumbers(phoneNumberArray)
            openPhoneNumberUnavailableModal()
         } else {
            message.success('Draft campaign applied successfully')
         }
      } catch (error) {
         console.error('Error applying draft campaign:', error)
         message.error('Failed to apply draft campaign')
      } finally {
         setIsLoading(false)
         mutate()
      }
   }

   const statusLabel = activeLeadSourceDetails?.status
   const statusColor =
      statusLabel === 'active'
         ? 'bg-emerald-500/15 text-emerald-400'
         : statusLabel === 'paused'
            ? 'bg-amber-500/15 text-amber-400'
            : 'bg-white/10 text-white/60'

   return (
      <div className="bg-[#9061DA]/[0.05] h-[60px] rounded-xl flex items-center px-3 justify-between">
         {/* Back button */}
         <div className="flex items-center gap-x-1">
            <Link
               to="/campaign"
               className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors shrink-0"
            >
               <Icon
                  name="arrow-right"
                  className="rotate-180 fill-white"
                  width={18}
                  height={18}
               />
            </Link>

            {/* Breadcrumb */}
            <nav className="flex items-center text-sm ml-3 min-w-0 overflow-hidden">
               <Link to="/dashboard" className="text-white/30 hover:text-white/50 transition-colors shrink-0">
                  Home
               </Link>
               <span className="text-white/20 mx-2 shrink-0">/</span>
               <Link to="/campaign" className="text-white/30 hover:text-white/50 transition-colors shrink-0">
                  Campaign
               </Link>
               {campaignName && (
                  <>
                     <span className="text-white/20 mx-2 shrink-0">/</span>
                     <span className="text-white font-medium truncate max-w-[200px]">
                        {campaignName}
                     </span>
                  </>
               )}
            </nav>
         </div>
         <div className="flex items-center gap-2">
            {/* Status badge */}
            {statusLabel && statusLabel !== 'draft' && (
               <span className={`text-xs font-medium px-3 py-1 rounded-full mr-3 capitalize shrink-0 ${statusColor}`}>
                  {statusLabel}
               </span>
            )}


            {/* Actions */}
            {showSaveButton &&
               activeLeadSourceDetails &&
               activeLeadSourceDetails?.status !== 'draft' && (
                  <Button
                     loading={isLoading}
                     onClick={ApplyDraftCampaign}
                     variant='secondary'
                     size='middle'
                  >
                     Save Changes
                  </Button>
               )}
            {activeLeadSourceDetails ? (
               activeLeadSourceDetails.status === 'draft' ? (
                  <Button
                     onClick={openCampaignActivationModal}
                     size='middle'
                  >
                     Activate Campaign
                  </Button>
               ) : (
                  <Button
                     size='middle'
                     loading={isStatusChanging}
                     onClick={onCampaignStatusChange}
                     icon={activeLeadSourceDetails.status === 'active' ? <Icon name="Pause" className="fill-white" /> : <Icon
                        name="Play"
                        className="scale-50 fill-white"
                     />}
                  >
                     {activeLeadSourceDetails.status === 'active' ? (
                        'Pause'
                     ) : (
                        'Resume'
                     )}
                  </Button>
               )
            ) : null}
         </div>
      </div>
   )
}
